import type { RevisionMeta } from '@/model/diff';
import type { FunnelParamsPatch } from '@/model/types';
import { API_BASE_URL, apiBase, writeHeaders } from './apiConfig';
import { PersistenceError } from './types';

/**
 * Reads and rewinds the revision log.
 *
 * Deliberately NOT part of `PersistencePort`. History only exists on the server —
 * localStorage has no notion of one — so folding it into the port would force
 * the local adapter to implement methods that can only ever return nothing. A
 * separate client that is simply absent when there is no API says the true thing,
 * and lets the panel show "history needs the scenario service" rather than an
 * empty timeline that looks like a scenario with no edits.
 */
export type HistoryClient = {
  listRevisions(scenarioId: string, limit?: number): Promise<RevisionMeta[]>;
  restore(
    scenarioId: string,
    version: number,
  ): Promise<{ version: number; params: FunnelParamsPatch }>;
};

type RevisionResponse = {
  revisions: {
    version: number;
    params: FunnelParamsPatch;
    op: RevisionMeta['op'];
    restoredFrom: number | null;
    sessionId: string | null;
    createdAt: string;
  }[];
};

export function createHistoryClient(baseUrl: string): HistoryClient {
  const base = baseUrl.replace(/\/+$/, '');
  const scenario = (id: string) => `${base}/api/scenarios/${encodeURIComponent(id)}`;

  return {
    async listRevisions(scenarioId, limit = 100) {
      let response: Response;
      try {
        response = await fetch(`${scenario(scenarioId)}/revisions?limit=${limit}`, {
          headers: { Accept: 'application/json' },
          credentials: 'omit',
        });
      } catch (cause) {
        throw new PersistenceError('Could not reach the scenario service', cause);
      }

      // A scenario with nothing saved has no history, which is not an error.
      if (response.status === 404) return [];
      if (!response.ok) {
        throw new PersistenceError(`Scenario service returned ${response.status}`);
      }

      const body = (await response.json()) as RevisionResponse;
      return body.revisions ?? [];
    },

    async restore(scenarioId, version) {
      let response: Response;
      try {
        response = await fetch(`${scenario(scenarioId)}/restore`, {
          method: 'POST',
          headers: writeHeaders(),
          credentials: 'omit',
          body: JSON.stringify({ version }),
        });
      } catch (cause) {
        throw new PersistenceError('Could not reach the scenario service', cause);
      }
      if (!response.ok) {
        throw new PersistenceError(`Could not restore version ${version}`);
      }
      return (await response.json()) as { version: number; params: FunnelParamsPatch };
    },
  };
}

/**
 * Null when no API is configured, which the panel reads as "there is no history
 * to show" rather than "this scenario has never been edited".
 */
export const historyClient: HistoryClient | null = API_BASE_URL
  ? createHistoryClient(apiBase())
  : null;
